import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useTranslation from "@hooks/use_translation";
import { useVerification } from "@hooks/use_verification";
import useTasks from "@hooks/use_tasks";
import TaskCard from "./TaskCard";
import ClearVerificationModal from "../../ClearVerificationModal";
import VerificationIcon from "@/sprout/icons/verification";
import type { GhrTask } from "@/types/task";
import JobInfo from "./JobInfo";
import { SecondaryButton, SecondaryButtonLink } from "@sprout/buttons";

interface TaskCardProps {
  task: GhrTask;
  statusText: string;
}

const IdentityVerificationTask: React.FC<TaskCardProps> = ({ task, statusText }) => {
  const { t: tTasks } = useTranslation("tasks");
  const { t: tClear } = useTranslation("clear_verification");
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const queryClient = useQueryClient();
  const { isRefetching } = useTasks();

  const { verifyWithClear } = useVerification({
    taskId: task.taskable_id,
    onSuccess: () => {
      setShowVerifyModal(false);
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  const openVerifyModal = () => {
    setShowVerifyModal(true);
  };

  const actionText = (() => {
    switch (task.status) {
      case "awaiting_user_input":
        return tClear("cta.continue_verification");
      case "processing_data":
        return tClear("status.processing_data.label");
      default:
        return tClear("cta.verify_identity");
    }
  })();

  useEffect(() => {
    // Only auto-open modal if we're not currently refetching tasks
    // This prevents modal flash when cached data briefly shows auto_open_modal: true
    if (task.auto_open_modal && !isRefetching && task.status !== "awaiting_user_input") {
      openVerifyModal();
    }
  }, [task.auto_open_modal, isRefetching, task.status]);

  const handleVerify = async () => {
    setShowVerifyModal(false);
    await verifyWithClear();
  };

  const VerificationButton = () => {
    if (task.status === "processing_data") {
      return (
        <SecondaryButton size="medium" disabled>
          {tClear("status.processing_data.label")}
        </SecondaryButton>
      );
    }

    if (task.status === "awaiting_user_input" && task.verification_url) {
      return (
        <SecondaryButtonLink size="medium" href={task.verification_url} external>
          {tClear("cta.continue_verification")}
        </SecondaryButtonLink>
      );
    }

    return (
      <SecondaryButton size="medium" onClick={openVerifyModal}>
        {tClear("cta.verify_identity")}
      </SecondaryButton>
    );
  };

  return (
    <>
      {!task.dismissed && (
        <TaskCard
          statusText={statusText}
          task={task}
          title={tTasks("identityVerification.title")}
          body={<JobInfo task={task} />}
          Modal={VerificationButton}
          actionText={actionText}
          icon={<VerificationIcon size="sm" />}
          theme={{
            colors: {
              background: "bg-white",
              border: "border-gray-700",
              iconBackground: "bg-gray-500",
            },
          }}
          dismissable={false}
          externalLink={false}
        />
      )}
      <ClearVerificationModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        onVerify={handleVerify}
        companyName={task.company_name}
      />
    </>
  );
};

export default IdentityVerificationTask;
